import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import config from '../config/config';
import { BookOpenCheck, Loader2, Download, AlertTriangle } from 'lucide-react';

const TrialBalance = () => {
  const [entries, setEntries] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({ start: '', end: '' });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('No auth token found.');
      setLoading(false);
      return;
    }

    axios.get(config.API_URL+'/api/pullJournalEntries', {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(res => {
      setEntries(res.data.data);
      setFiltered(res.data.data);
      setLoading(false);
    })
    .catch(() => {
      setError('Failed to load journal entries');
      setLoading(false);
    });
  }, []);

  const applyFilter = () => {
    if (!filter.start || !filter.end) return;
    const start = new Date(filter.start);
    const end = new Date(filter.end);
    const filteredEntries = entries.filter(e => {
      const date = new Date(e.date);
      return date >= start && date <= end;
    });
    setFiltered(filteredEntries);
  };

  const groupedEntries = filtered.reduce((acc, entry) => {
    const key = `${entry.reference_code || 'N/A'}-${entry.account?.name || 'Account #' + entry.account_id}`;
    if (!acc[key]) {
      acc[key] = {
        reference_code: entry.reference_code || 'N/A',
        account_name: entry.name || 'Account #' + entry.account_id,
        debit: 0,
        credit: 0
      };
    }
    acc[key].debit += parseFloat(entry.debit || 0);
    acc[key].credit += parseFloat(entry.credit || 0);
    return acc;
  }, {});

  const groupedArray = Object.values(groupedEntries);

  const totalDebit = groupedArray.reduce((sum, e) => sum + e.debit, 0);
  const totalCredit = groupedArray.reduce((sum, e) => sum + e.credit, 0);

  const exportToExcel = () => {
    const sheet = XLSX.utils.json_to_sheet(groupedArray.map(e => ({
      'Reference Code': e.reference_code,
      'Account': e.account_name,
      'Debit (KES)': e.debit,
      'Credit (KES)': e.credit
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, 'TrialBalance');
    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buffer]), 'Trial_Balance.xlsx');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const logo = new Image();
    logo.src = '/logo.png';

    logo.onload = () => {
      doc.addImage(logo, 'PNG', 14, 10, 30, 15);
      doc.text('Trial Balance Report', 105, 20, null, null, 'center');

      autoTable(doc, {
        startY: 30,
        head: [['Ref Code', 'Account', 'Debit (KES)', 'Credit (KES)']],
        body: groupedArray.map(e => [
          e.reference_code,
          e.account_name,
          e.debit.toFixed(2),
          e.credit.toFixed(2)
        ]),
        foot: [[
          '',
          'Total',
          totalDebit.toFixed(2),
          totalCredit.toFixed(2)
        ]]
      });

      doc.save('Trial_Balance.pdf');
    };
  };

  if (loading) {
    return <div className="flex justify-center mt-10"><Loader2 className="animate-spin w-6 h-6 text-blue-500" /></div>;
  }

  if (error) {
    return <div className="text-red-600 mt-10 flex justify-center items-center gap-2"><AlertTriangle className="w-5 h-5" />{error}</div>;
  }

  return (
    <div className="max-w-5xl mx-auto p-6 bg-white rounded-xl shadow mt-10">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-2 text-xl font-semibold text-gray-700">
          <BookOpenCheck className="w-6 h-6 text-blue-500" />
          Trial Balance
        </div>
        <div className="flex gap-2 flex-wrap">
        {/*  <input type="date" value={filter.start} onChange={e => setFilter({ ...filter, start: e.target.value })} className="border px-2 py-1 rounded text-sm" />
          <input type="date" value={filter.end} onChange={e => setFilter({ ...filter, end: e.target.value })} className="border px-2 py-1 rounded text-sm" />
          <button onClick={applyFilter} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm">Filter</button>*/}
          <button onClick={exportToExcel} className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm flex items-center gap-1"><Download className="w-4 h-4" /> Excel</button>
         {/* <button onClick={exportToPDF} className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm flex items-center gap-1"><Download className="w-4 h-4" /> PDF</button>*/}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm text-left border">
          <thead className="bg-gray-100 text-gray-700 uppercase">
            <tr>
              <th className="px-4 py-2 border">Account</th>
              <th className="px-4 py-2 border text-right">Debit (KES)</th>
              <th className="px-4 py-2 border text-right">Credit (KES)</th>
            </tr>
          </thead>
          <tbody>
            {groupedArray.map((entry, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-4 py-2 border">{entry.account_name}</td>
                <td className="px-4 py-2 border text-right text-green-600">{entry.debit.toLocaleString()}</td>
                <td className="px-4 py-2 border text-right text-red-600">{entry.credit.toLocaleString()}</td>
              </tr>
            ))}
            <tr className="bg-gray-100 font-semibold">
              <td colSpan="1" className="px-4 py-2 border text-right">Total</td>
              <td className="px-4 py-2 border text-right text-green-700">{totalDebit.toLocaleString()}</td>
              <td className="px-4 py-2 border text-right text-red-700">{totalCredit.toLocaleString()}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TrialBalance;
